import axios from 'axios';
import { supabase } from './supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple request cache to prevent duplicate calls within 30s
const cache = new Map();
const CACHE_TTL = 30000;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// Attach Supabase auth token
api.interceptors.request.use(async (config) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
  } catch {}
  return config;
});

// Response interceptor: cache GET responses, handle 429
api.interceptors.response.use(
  (response) => {
    if (response.config.method === 'get') {
      const key = response.config.url + (response.config.params ? JSON.stringify(response.config.params) : '');
      cache.set(key, { data: response, ts: Date.now() });
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 429) {
      // Rate limited — wait and retry once
      await new Promise(r => setTimeout(r, 2000));
      return api.request(error.config);
    }
    return Promise.reject(error);
  }
);

// Cached GET helper
export const cachedGet = async (url, config = {}) => {
  const key = url + (config.params ? JSON.stringify(config.params) : '');
  const cached = cache.get(key);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return cached.data;
  }
  return api.get(url, config);
};

export const clearCache = () => cache.clear();

export default api;
export { API, BACKEND_URL };
